import {
  Button,
  Callout,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  Skeleton,
  cn,
} from '@provenance/design';
import { analyseImageBackground, clearLightBackground } from '@provenance/shared';
import { AlertTriangle } from 'lucide-react';
import { useEffect, useState } from 'react';
import { ApiError } from '../../lib/api';

/**
 * ════════════════════════════════════════════════════════════════════════════
 * TRANSPARENT SEALS AND SIGNATURES (T-126)
 *
 * A seal or signature with a solid white background prints as a white box over the
 * certificate — over the form's own text, the dotted signature line, the date. BCQ's ACCI
 * seal did exactly that. This offers a transparent version, with the before and after
 * side by side on a checkerboard so the difference is visible rather than asserted.
 *
 * Never automatic: the administrator chooses. The clean-up is `clearLightBackground` from
 * `@provenance/shared`, the one implementation, run here on the pixels the browser decoded.
 * ════════════════════════════════════════════════════════════════════════════
 */

export type CleanedImage =
  | { state: 'checking' }
  | { state: 'unreadable' }
  | { state: 'clear'; original: File }
  | { state: 'solid'; original: File; cleaned: File; originalUrl: string; cleanedUrl: string };

/** Decodes an image, and when its background is solid, prepares a transparent PNG of it. */
export function useCleanedImage(source: Blob | null, filename: string): CleanedImage | null {
  const [result, setResult] = useState<CleanedImage | null>(null);

  useEffect(() => {
    if (!source) {
      setResult(null);
      return;
    }
    let live = true;
    const urls: string[] = [];
    setResult({ state: 'checking' });

    void (async () => {
      try {
        const bitmap = await createImageBitmap(source);
        const canvas = document.createElement('canvas');
        canvas.width = bitmap.width;
        canvas.height = bitmap.height;
        const context = canvas.getContext('2d', { willReadFrequently: true });
        if (!context) throw new Error('no 2D context');
        context.drawImage(bitmap, 0, 0);
        const pixels = context.getImageData(0, 0, canvas.width, canvas.height);
        const original = source instanceof File ? source : new File([source], filename, { type: source.type });

        if (!analyseImageBackground(pixels.data, canvas.width, canvas.height).solid) {
          if (live) setResult({ state: 'clear', original });
          return;
        }

        clearLightBackground(pixels.data);
        context.putImageData(pixels, 0, 0);
        const png = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'));
        if (!png) throw new Error('could not encode PNG');
        const cleaned = new File([png], `${filename.replace(/\.[^.]+$/, '')}-transparent.png`, { type: 'image/png' });
        urls.push(URL.createObjectURL(original), URL.createObjectURL(cleaned));
        if (live) setResult({ state: 'solid', original, cleaned, originalUrl: urls[0]!, cleanedUrl: urls[1]! });
      } catch {
        // Nothing we can decode is nothing we can clean. The server still validates it.
        if (live) setResult({ state: 'unreadable' });
      }
    })();

    return () => {
      live = false;
      urls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [source, filename]);

  return result;
}

const CHECKERBOARD =
  'bg-[conic-gradient(var(--pv-line)_25%,var(--pv-card)_0_50%,var(--pv-line)_0_75%,var(--pv-card)_0)] [background-size:14px_14px]';

/**
 * The before/after pair — and, when `onChange` is given, the choice between them. Without
 * it the pair is only shown, for a dialog whose button already makes the choice.
 */
export function BackgroundChoice({
  result,
  useCleaned,
  onChange,
  what,
}: {
  result: Extract<CleanedImage, { state: 'solid' }>;
  useCleaned: boolean;
  onChange?: (useCleaned: boolean) => void;
  what: 'seal' | 'signature';
}) {
  const option = (cleaned: boolean, url: string, title: string, caption: string) => (
    <label
      className={cn(
        'flex flex-col gap-2 rounded-[var(--pv-radius)] border-2 p-2 focus-within:shadow-[var(--pv-ring)]',
        onChange && 'cursor-pointer',
        useCleaned === cleaned ? 'border-[var(--pv-leaf-deep)]' : 'border-[var(--pv-line)]',
      )}
    >
      <span className={cn('grid h-32 place-items-center rounded-[var(--pv-radius-sm)] p-2', CHECKERBOARD)}>
        <img src={url} alt={`${title}: the ${what}`} className="max-h-full max-w-full object-contain" />
      </span>
      <span className="flex items-start gap-2">
        {onChange && (
          <input
            type="radio"
            name={`background-${what}`}
            checked={useCleaned === cleaned}
            onChange={() => onChange(cleaned)}
            className="mt-0.5 accent-[var(--pv-brand)]"
          />
        )}
        <span>
          <span className="block [font-size:13px] font-semibold">{title}</span>
          <span className="block [font-size:12px] leading-snug text-[var(--pv-muted)]">{caption}</span>
        </span>
      </span>
    </label>
  );

  return (
    <div className="flex flex-col gap-3">
      <Callout tone="warn" icon={<AlertTriangle className="size-4" />} title="This image has a solid background">
        Printed as it is, the white prints too — as a box that hides anything beneath it on the
        certificate, including the form's own text and lines. The checkerboard shows through
        wherever the image is transparent.
      </Callout>
      <div className="grid gap-3 sm:grid-cols-2">
        {option(true, result.cleanedUrl, 'Transparent version', 'Only the ink prints. Colours and edges are kept.')}
        {option(false, result.originalUrl, 'As uploaded', 'Prints a white box wherever the image is white.')}
      </div>
    </div>
  );
}

/**
 * The one-click fix for a seal or signature already on file.
 *
 * `load` fetches the image through our own origin — object storage is another origin, and
 * a browser will not let a page read those pixels. `save` uploads the transparent version
 * as a new image; the old one is kept and named in the audit trail.
 */
export function FixBackgroundDialog({
  open,
  onOpenChange,
  what,
  name,
  load,
  save,
  onSaved,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  what: 'seal' | 'signature';
  name: string;
  load: () => Promise<Blob>;
  save: (file: File) => Promise<void>;
  onSaved: () => void;
}) {
  const [source, setSource] = useState<Blob | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const result = useCleanedImage(source, `${name}.png`);

  useEffect(() => {
    if (!open) {
      setSource(null);
      setError(null);
      return;
    }
    let live = true;
    load()
      .then((blob) => live && setSource(blob))
      .catch((err: unknown) => live && setError(err instanceof ApiError ? err.message : 'The image could not be loaded.'));
    return () => {
      live = false;
    };
    // `load` is a fresh closure each render; the dialog opening is the event.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const apply = async () => {
    if (result?.state !== 'solid') return;
    setSaving(true);
    setError(null);
    try {
      await save(result.cleaned);
      onSaved();
      onOpenChange(false);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'The transparent version could not be saved.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader
          title={`Make the ${what} background transparent`}
          description={`${name}. Certificates already issued are not changed; the next one uses the new image.`}
        />
        <DialogBody>
          {error ? (
            <Callout tone="stop" icon={<AlertTriangle className="size-4" />} title="Something went wrong">
              {error}
            </Callout>
          ) : !result || result.state === 'checking' ? (
            <Skeleton className="h-48" />
          ) : result.state === 'solid' ? (
            <BackgroundChoice result={result} useCleaned what={what} />
          ) : result.state === 'clear' ? (
            <p className="[font-size:13px] text-[var(--pv-muted)]">
              This image already has a transparent background. Nothing to fix.
            </p>
          ) : (
            <p className="[font-size:13px] text-[var(--pv-muted)]">This image could not be read in the browser.</p>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button variant="primary" disabled={result?.state !== 'solid'} loading={saving} onClick={() => void apply()}>
            Use the transparent version
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
